'use client';

import { useQuery } from '@tanstack/react-query';
import { authApi } from '@/lib/api/auth';
import {
  hasAtLeast,
  insufficientRoleReason,
  type TenantRole,
} from '@/lib/permissions';

/**
 * The caller's role in the active tenant, and a `can()` helper for gating UI.
 *
 * Reuses the `['user-tenants']` query the tenant switcher already runs, so this
 * costs no extra request no matter how many components call it — React Query
 * dedupes on the key.
 *
 * Gating here is a courtesy, not a control: `backend/src/api/permissions.py`
 * enforces every one of these rules server-side. The point is that a viewer
 * sees a disabled button with a reason rather than discovering the limit after
 * filling in a form.
 */
export function usePermissions() {
  const { data, isLoading } = useQuery({
    queryKey: ['user-tenants'],
    queryFn: () => authApi.getTenants(),
  });

  const role = (data?.tenants.find((t) => t.id === data.active_tenant_id)
    ?.role ?? null) as TenantRole | null;

  return {
    role,
    isLoading,
    /** Whether the caller holds `required` or higher. False while loading. */
    can: (required: TenantRole) => hasAtLeast(role, required),
    /** Tooltip text explaining why a control is disabled. */
    reasonFor: (required: TenantRole) => insufficientRoleReason(role, required),
  };
}
